/**
 * Hvor mange SMS'er bliver beskeden til?
 *
 * En SMS sendes med et af to tegnsæt. GSM-7 er det almindelige og har plads til 160 tegn. Danske æ, ø og å er
 * med i GSM-7, så de koster ikke ekstra - men tegn uden for tegnsættet (fx emoji, tankestreg og "krøllede"
 * anførselstegn) tvinger hele beskeden over i UCS-2, hvor der kun er plads til 70 tegn.
 *
 * Er beskeden for lang til én SMS, deles den, og hver del bruger lidt plads på at fortælle, hvordan den skal
 * samles igen: 153 tegn pr. del i GSM-7 og 67 i UCS-2.
 *
 * Tallet er et skøn: den færdige besked afhænger af modtagerens navn og af det korte link, der laves, når
 * invitationen sendes.
 */

/** GSM 03.38 - tegnene der kan sendes som almindelig SMS */
const GSM7 =
  "@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !\"#¤%&'()*+,-./0123456789:;<=>?" +
  "¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà";

/** Disse fylder to pladser, fordi de skrives med et skiftetegn foran */
const GSM7_EXTENDED = "^{}\\[~]|€";

export interface SmsLength {
  /** Antal pladser beskeden fylder */
  characters: number;
  /** Antal SMS'er den bliver til */
  messages: number;
  /** true = beskeden indeholder et tegn, der halverer pladsen */
  unicode: boolean;
  /** Tegnene der tvinger UCS-2, så de kan vises for den, der skriver */
  offenders: string[];
  /** Pladser tilbage i den nuværende sidste SMS */
  remaining: number;
}

export const smsLength = (text: string): SmsLength => {
  // Array.from tæller tegn som brugeren ser dem - også dem der fylder to pladser i JavaScript, fx emoji
  const characters = Array.from(String(text || ""));
  const offenders = Array.from(new Set(characters.filter((character) => !GSM7.includes(character) && !GSM7_EXTENDED.includes(character))));
  const unicode = offenders.length > 0;

  const length = unicode
    ? characters.length
    : characters.reduce((sum, character) => sum + (GSM7_EXTENDED.includes(character) ? 2 : 1), 0);

  const single = unicode ? 70 : 160;
  const perPart = unicode ? 67 : 153;
  const messages = length === 0 ? 1 : length <= single ? 1 : Math.ceil(length / perPart);
  const room = messages === 1 ? single : perPart * messages;
  return { characters: length, messages, unicode, offenders, remaining: room - length };
};

/** Eksempelværdier, så forhåndsvisningen ligner en rigtig besked */
export const SMS_EXAMPLES: Record<string, string> = {
  FIRSTNAME: "Mette",
  LASTNAME: "Jensen",
  PHONE: "20 12 34 56",
  LINK: "https://s.breefer.dk/a1b2c",
  PRIVACYLINK: "https://s.breefer.dk/p9x7z",
  BREEFEXPIRYDATE: "14. oktober",
  BREEFSTARTDATE: "7. oktober",
  TENANTFIRSTNAME: "René",
  TENANTLASTNAME: "Horslev",
  USERPLACEMENT: "3",
  BREEFSCORE: "2450",
  USERSCORE: "1980",
  BREEFNAME: "Sikkerhed på kontoret",
  PRICE: "et gavekort",
  NUMBEROFPARTICIPANTS: "24",
};

export const withExamples = (text: string): string =>
  String(text || "").replace(/\|\*([A-Z0-9_]+)\*\|/g, (whole, name) => SMS_EXAMPLES[name] ?? whole);
